import { type HttpErrorResponse } from '@angular/common/http';
import { type DeepPartial } from '@core';
import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { type ImageDocument, type SpaceplanTabType } from '@space-plan/models';

import { type EntitySummary, type FloorSpaceModel, type LocationBaseModel, type LocationData, type LocationFullData, type RoomSpaceModel, type Space } from '../models';

export const LocationActions = createActionGroup({
  events: {
    'Add Floor': props<{ data: Partial<Space>; locationId: string; }>(),
    'Add Floor Error': props<{ error: HttpErrorResponse; }>(),
    'Add Floor Success': props<{ floor: FloorSpaceModel; }>(),
    'Add Location': props<{ clientId: string; newLocation: LocationBaseModel; }>(),
    'Add Location Success': props<{ newLocation: LocationData; }>(),
    'Add Room': props<{ data: Partial<Space>; locationId: string; }>(),
    'Add Room Error': props<{ error: HttpErrorResponse; }>(),
    'Add Room Success': props<{ room: RoomSpaceModel; }>(),
    'Delete Floor': props<{ floorId: string; locationId: string; navigateToParent: boolean; }>(),
    'Delete Floor Error': props<{ error: HttpErrorResponse; }>(),
    'Delete Floorplan': props<{ fileName: string; locationId: string; spaceId: string; }>(),
    'Delete Floorplan Error': props<{ error: HttpErrorResponse; }>(),
    'Delete Floorplan Success': props<{ spaceId: string; }>(),
    'Delete Location': props<{ locationId: string; navigateToParent: boolean; }>(),
    'Delete Location Error': props<{ error: HttpErrorResponse; }>(),
    'Delete Room': props<{ floorId: string; locationId: string; navigateToParent: boolean; roomId: string; }>(),
    'Delete Room Error': props<{ error: HttpErrorResponse; }>(),
    'Delete Roomplan': props<{ fileName: string; floorId: string; locationId: string; roomId: string; }>(),
    'Delete Roomplan Error': props<{ error: HttpErrorResponse; }>(),
    'Delete Roomplan Success': props<{ fileName: string; floorId: string; roomId: string; }>(),
    'Drop Floor': props<{ floorId: string; }>(),
    'Drop Location': props<{ locationId: string; }>(),
    'Drop Room': props<{ floorId: string; roomId: string; }>(),
    'Get Location State': props<{ excludeArchived: boolean; locationId: string; }>(),
    'Get Location State Success': props<{ locationState: LocationFullData; }>(),
    'Get Space Documents': props<{ floorId: string; locationId: string; roomId?: string; }>(),
    'Get Space Documents Error': emptyProps(),
    'Get Space Documents Success': props<{ documents: ImageDocument[]; floorId: string; roomId?: string; }>(),
    'Load Locations': props<{ clientId: string; }>(),
    'Load Locations Summaries Error': emptyProps(),
    'Load Locations Summaries Success': props<{ data: { changes: EntitySummary; id: string; }[]; }>(),
    'Location Error': props<{ locationError: HttpErrorResponse; }>(),
    'Locations Loaded': props<{ location: LocationData[]; }>(),
    'Reorder Floors': props<{ floors: Partial<Space>[]; locationId: string; }>(),
    'Reorder Floors Error': emptyProps(),
    'Reorder Floors Success': emptyProps(),
    'Reset Default Locations State': emptyProps(),
    'Reset Location State': emptyProps(),
    'Update Floor': props<{ data: DeepPartial<Space>; locationId: string; spaceId: string; }>(),
    'Update Floor Error': props<{ error: HttpErrorResponse; }>(),
    'Update Floor Success': props<{ floor: FloorSpaceModel; locationId: string; }>(),
    'Update Location': props<{ clientId: string; data: DeepPartial<LocationData>; locationId: string; }>(),
    'Update Location Success': props<{ location: LocationData; }>(),
    'Update Room': props<{ data: DeepPartial<Space>; locationId: string; spaceId: string; }>(),
    'Update Room Error': props<{ error: HttpErrorResponse; }>(),
    'Update Room Success': props<{ room: RoomSpaceModel; }>(),
    'Upload Floorplan': props<{ file: File; locationId: string; spaceId: string; tab: SpaceplanTabType }>(),
    'Upload Floorplan Error': props<{ error: HttpErrorResponse; }>(),
    'Upload Floorplan Success': props<{ document: ImageDocument; spaceId: string; }>(),
    'Upload Plan': props<{ file: File; floorId: string; locationId: string; roomId?: string; tab: SpaceplanTabType; }>(),
    'Upload Plan Error': props<{ error: HttpErrorResponse; }>(),
    'Upload Plan Success': props<{ document: ImageDocument; floorId: string; roomId?: string; }>(),
  },
  source: 'Client Users',
});
